import { useAuth } from '@auth/useAuth'
import {
  getTenantMember,
  RecoveryMethodStatusEnum,
  TenantMember,
  TenantMemberEmbeds,
} from '@kudos/http-client'
import { useQuery } from '@tanstack/react-query'

export const CURRENT_MEMBER_QK = 'getTenantMemberQueryKey'

type CurrentMember = {
  member: TenantMember | null
  userId: string | null
  isManager: boolean
  /** 2-character ISO country code from the member's staff record, if known. */
  country: string | null
  /**
   * Password recovery method status for deskless users. `null` for non-deskless
   * users (those with an account email). Only populated when the
   * `emailAndRecoveryMethodStatus` embed is requested (the default).
   */
  recoveryMethodStatus: RecoveryMethodStatusEnum | null
  isFetched: boolean
}

/**
 * Default embeds for the current member. `emailAndRecoveryMethodStatus` is a
 * superset of the `email` embed that also returns `recoveryMethodStatus`, so
 * consumers can read the recovery prompt state without an additional `/me`
 * fetch. `bio` is here for the same reason: the profile page and the profile
 * settings both want it, and a second embed list would mean a second request.
 * Reuse this (and extend it) so callers share a single cached request.
 */
export const DEFAULT_MEMBER_EMBEDS: TenantMemberEmbeds[] = [
  'avatar',
  'bannerUrls',
  'publicStaffProfile',
  'hasReports',
  'emailAndRecoveryMethodStatus',
  'bio',
]

/**
 * Embeds for the home page and the profile page (`/me`). The query key is built
 * from the embeds and neither page renders anything until the member arrives, so
 * they have to ask for the identical list — any difference leaves the profile
 * blank while a fresh `/me` fetch runs after "View profile". `events` is here
 * for the contextual greeting on home and the celebration treatment on the
 * profile. Anything only one of the two needs belongs in its own query rather
 * than in this list, so home does not pay for it.
 */
export const PROFILE_MEMBER_EMBEDS: TenantMemberEmbeds[] = [...DEFAULT_MEMBER_EMBEDS, 'events']

/**
 * Get the TenantMember entity and associated user id for the current member.
 * @returns Tuple of the Member & loaded status
 */
export const useCurrentMember = (
  embeds: TenantMemberEmbeds[] = DEFAULT_MEMBER_EMBEDS,
): CurrentMember => {
  const { userId, isAuthenticated, isTenantScope } = useAuth()
  const {
    data: member,
    isFetched,
    isError,
  } = useQuery<TenantMember>({
    queryKey: [CURRENT_MEMBER_QK, userId, ...embeds],
    queryFn: () => getTenantMember('me', { embeds }),
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated && isTenantScope && !!userId,
  })

  if (!userId || isError) {
    return {
      member: null,
      userId: null,
      isManager: false,
      country: null,
      recoveryMethodStatus: null,
      isFetched,
    }
  }

  return {
    member: member || null,
    userId: userId || null,
    isManager: member?.hasReports || false,
    country: member?.country ?? null,
    recoveryMethodStatus: member?.recoveryMethodStatus ?? null,
    isFetched,
  }
}
